import { TemperatureDataRow } from '../models/temperature-data-row';
import { rowOnLinerGrid } from './telemetry-downsample';
import { TelemetryRingBuffer } from './telemetry-ring-buffer';

/**
 * Incremental accumulators for the Curing screen's derived series.
 *
 * What these replace
 * ------------------
 * updateCuringData() used to rebuild SIX `ticks x width` matrices from scratch on
 * every RT poll (max temperature, time-above-curing, and time-above-X for up to
 * four heat inputs). That was O(ticks x width) per poll — growing linearly with
 * cure duration — and retained unboundedly.
 *
 * Every one of those quantities is a pure left-fold over ticks:
 *
 *   max[j]   = i == 0 ? TR[j] : max(TR[j], prevMax[j])
 *   above[j] = i == 0 ? 0
 *            : TR[j] <  threshold ? prevAbove[j]
 *            : prevAbove[j] + (UT[i] - UT[i-1])
 *
 * so they can be folded one tick at a time in O(width). This class keeps the
 * RUNNING value per position (cheap, width-sized) plus a bounded ring of per-tick
 * snapshots for scrubbing.
 *
 * Why the running state must persist across eviction
 * --------------------------------------------------
 * These folds are cumulative FROM TICK 0. Recomputing them over only the retained
 * window would restart the accumulation and silently under-report both peak
 * temperature and cure time. So the running arrays are never rebuilt from the
 * retained window — they are only ever rebuilt from a full backfill response,
 * which the server returns from tick 0. Snapshots older than the retention window
 * are dropped for display purposes, but the accumulation itself is unbroken.
 */

export interface CuringThresholds {
  /** The project curing temperature (ProjectTemperatureData.CT). */
  curing: number;
  /** Heat-input thresholds from HMC[0..3]; >= DISABLED_THRESHOLD means "off". */
  x1: number;
  x2: number;
  x3: number;
  x4: number;
}

/**
 * The existing convention: a threshold of 1000 (or the default substituted when
 * HMC has no entry at that index) means the series is not shown.
 */
export const DISABLED_THRESHOLD = 1000;

export function isEnabled(threshold: number): boolean {
  return Number.isFinite(threshold) && threshold < DISABLED_THRESHOLD;
}

/** One tick's worth of derived values, aligned to [startDataPoint, endDataPoint). */
export interface CuringSnapshot {
  max: number[];
  aboveCuring: number[];
  above1?: number[];
  above2?: number[];
  above3?: number[];
  above4?: number[];
}

export class CuringAccumulators {
  private width = 0;
  private thresholds: CuringThresholds = {
    curing: DISABLED_THRESHOLD,
    x1: DISABLED_THRESHOLD,
    x2: DISABLED_THRESHOLD,
    x3: DISABLED_THRESHOLD,
    x4: DISABLED_THRESHOLD,
  };

  // Running (cumulative from tick 0) state — width-sized, never dropped.
  private accMax: number[] = [];
  private accCuring: number[] = [];
  private accX1: number[] = [];
  private accX2: number[] = [];
  private accX3: number[] = [];
  private accX4: number[] = [];
  private prevUT = 0;
  private folded = 0;

  /** Bounded per-tick snapshots for scrubbing. */
  private snapshots: TelemetryRingBuffer<CuringSnapshot>;

  constructor(readonly capacity: number) {
    this.snapshots = new TelemetryRingBuffer<CuringSnapshot>(capacity);
  }

  get totalCount(): number {
    return this.snapshots.totalCount;
  }
  get retainedCount(): number {
    return this.snapshots.retainedCount;
  }
  get firstIndex(): number {
    return this.snapshots.firstIndex;
  }
  /**
   * How many ticks have been folded — which IS the absolute index of the next
   * one, because the fold sequence starts at tick 0 and `fold()` now advances it
   * for every row it is offered. Public so a caller can ask "am I level with the
   * store?" without reaching into private state.
   */
  get foldedCount(): number {
    return this.folded;
  }
  has(tick: number): boolean {
    return this.snapshots.has(tick);
  }
  at(tick: number): CuringSnapshot | undefined {
    return this.snapshots.get(tick);
  }

  /** Closest retained snapshot — the never-blank path. */
  nearest(tick: number): CuringSnapshot | undefined {
    return this.snapshots.nearest(tick)?.row;
  }

  /**
   * Match the snapshot window to the row window.
   *
   * These two MUST be sized together. The rows are resized from the adaptive
   * retention budget while this stayed at its fixed construction capacity of 200,
   * so past ~200 ticks every scrub to an older tick found a row but no snapshot —
   * and the renderer treated that as evicted history, blanked the chart and asked
   * for a full backfill. Sizing them together is what stops the miss happening at
   * all; the renderer's own never-blank handling is the backstop.
   */
  resizeTo(capacity: number): void {
    this.snapshots.resizeTo(capacity);
  }

  /**
   * Reset for a new project / new window geometry. Drops all running state, so
   * the next fold sequence must start at tick 0.
   */
  configure(width: number, thresholds: CuringThresholds): void {
    this.width = Math.max(0, width);
    this.thresholds = { ...thresholds };
    this.accMax = new Array<number>(this.width).fill(0);
    this.accCuring = new Array<number>(this.width).fill(0);
    this.accX1 = new Array<number>(this.width).fill(0);
    this.accX2 = new Array<number>(this.width).fill(0);
    this.accX3 = new Array<number>(this.width).fill(0);
    this.accX4 = new Array<number>(this.width).fill(0);
    this.prevUT = 0;
    this.folded = 0;
    this.snapshots.clear();
  }

  /**
   * Fold one tick. O(width). `offset` is startDataPoint — the row's TR is indexed
   * absolutely, the accumulators relatively.
   */
  fold(row: TemperatureDataRow, offset: number): void {
    if (this.width === 0) {
      // Not configured yet: there is no accumulator to fold INTO, and no index
      // space to stay level with either. Nothing to carry forward.
      return;
    }
    if (!row?.TR) {
      // A ROW WE CANNOT READ STILL COSTS A TICK.
      //
      // This used to `return` alongside the width check, which left `folded`
      // one behind the store permanently — and the caller indexes this
      // accumulator BY THE STORE'S absolute index, so one refused row silently
      // misaligns every later read. See max-accumulator-store-alignment.spec.ts.
      //
      // Carrying the running state forward keeps the two spaces level and is the
      // same answer the accumulator would give if the row were absent: the next
      // real row's `dt` spans the gap, so no time is credited against values we
      // never saw. `prevUT` deliberately stays put for that reason.
      //
      // NOTE this is a HAZARD WITH NO PROVEN TRIGGER: the model declares
      // `TR: number[]` non-optional, and the blank rows that do occur (S3640's
      // last four, all -273.15) carry a PRESENT array -- `isNoReading` tests
      // values, this tests the array -- so they fold normally and never reach
      // here. Fixed because the invariant should hold whatever arrives, not
      // because anything is known to send it.
      this.carryForward();
      return;
    }
    // Rows arrive either as the whole fiber array (position = sample index) or as
    // a decimated / liner-TRIMMED slice carrying SIX. Only the first can be read
    // at `TR[offset + k]`; project onto the liner grid so both fold identically.
    const linerValues = rowOnLinerGrid(
      row.TR,
      row.SIX,
      offset,
      offset + this.width
    );
    const first = this.folded === 0;
    // SECONDS. `UT` is unix MILLISECONDS — DataServices sets it with
    // ToUnixTimeMilliseconds(), and main-layout divides it by 1000 to get an
    // elapsed time — so folding the raw difference accumulated milliseconds while
    // both consumers of the result, the right-hand axis label provider and the
    // rollover tooltip, are formatDurationInSecToHHMMSS_Custom. Every time-over
    // value was therefore 1000x its true size: a band that had genuinely been
    // above threshold for 1 h 44 m read as 69 days.
    //
    // Converting here rather than at the two display sites keeps ONE unit in the
    // accumulator — the same reason the server converts temperature once, at the
    // response boundary.
    const dt = first ? 0 : (row.UT - this.prevUT) / 1000;

    const t = this.thresholds;
    const useX1 = isEnabled(t.x1);
    const useX2 = isEnabled(t.x2);
    const useX3 = isEnabled(t.x3);
    const useX4 = isEnabled(t.x4);

    const max = new Array<number>(this.width);
    const aboveCuring = new Array<number>(this.width);
    const above1 = useX1 ? new Array<number>(this.width) : undefined;
    const above2 = useX2 ? new Array<number>(this.width) : undefined;
    const above3 = useX3 ? new Array<number>(this.width) : undefined;
    const above4 = useX4 ? new Array<number>(this.width) : undefined;

    for (let k = 0; k < this.width; k++) {
      const v = linerValues[k];

      // Max temperature — running max, seeded by the first tick's value.
      this.accMax[k] = first ? v : Math.max(v, this.accMax[k]);
      max[k] = this.accMax[k];

      // Time above each threshold. The first tick contributes 0 (no interval yet),
      // matching the original i == 0 branch.
      if (!first && v >= t.curing) {
        this.accCuring[k] += dt;
      }
      aboveCuring[k] = this.accCuring[k];

      if (above1) {
        if (!first && v >= t.x1) this.accX1[k] += dt;
        above1[k] = this.accX1[k];
      }
      if (above2) {
        if (!first && v >= t.x2) this.accX2[k] += dt;
        above2[k] = this.accX2[k];
      }
      if (above3) {
        if (!first && v >= t.x3) this.accX3[k] += dt;
        above3[k] = this.accX3[k];
      }
      if (above4) {
        if (!first && v >= t.x4) this.accX4[k] += dt;
        above4[k] = this.accX4[k];
      }
    }

    this.snapshots.push({ max, aboveCuring, above1, above2, above3, above4 });
    this.prevUT = row.UT;
    this.folded++;
  }

  /**
   * Push a snapshot identical to the current running state, advancing `folded`.
   * The tick happened; we just could not read it.
   */
  private carryForward(): void {
    this.snapshots.push({
      max: this.accMax.slice(),
      aboveCuring: this.accCuring.slice(),
      above1: isEnabled(this.thresholds.x1) ? this.accX1.slice() : undefined,
      above2: isEnabled(this.thresholds.x2) ? this.accX2.slice() : undefined,
      above3: isEnabled(this.thresholds.x3) ? this.accX3.slice() : undefined,
      above4: isEnabled(this.thresholds.x4) ? this.accX4.slice() : undefined,
    });
    this.folded++;
  }

  /**
   * Fold whatever the store holds that this accumulator has not seen.
   *
   * WHY THIS EXISTS. The caller reads snapshots at the STORE's absolute index
   * (`temperatureDataRows.nextIndex - 1`), so the two must stay level. They do
   * not, because `projectService.temperatureData` is a BehaviorSubject: a
   * component built by an in-SPA navigation subscribes and is immediately handed
   * the LAST EMITTED payload, which may predate the store by however many live
   * ticks have arrived since. Rebuilding from that payload leaves this
   * accumulator exactly that many rows behind, permanently -- the live path then
   * appends one row and folds one row, so the gap is conserved, never closed.
   * Measured on a live cure: five rows behind after a six-minute soak, held
   * across four consecutive ticks.
   *
   * THE STORE IS THE AUTHORITY. It subscribes ONCE, at connect(), which is
   * idempotent, so it never re-reads a replayed payload and is current by
   * construction. Rebuild from the payload for cumulative correctness -- it
   * carries the cure from tick 0, and reset() preserves absolute indices, so
   * payload index i IS absolute tick i -- then call this to take the rows the
   * payload lacked. Rebuilding from the store ALONE would be wrong: its retained
   * window is not the cure, so any evicted tick would drop out of the running
   * max.
   *
   * A GAP IT CANNOT CLOSE IS NOT THE SAME AS A GAP IT JUST CLOSED, and the two
   * must not look alike — that confusion IS the defect this method exists to
   * fix. If the rows we are missing have already been evicted
   * (`rows.firstIndex > folded`) there is nothing to fold from and only a fresh
   * backfill can repair it, so that case says so, loudly, and reports
   * `closed: false`.
   *
   * It is reachable. At MI=60 the 600-row window is ten hours, but at the 5-10 s
   * sampling the UV case needs it is under an hour, and a stale replay plus a
   * long soak on a fast cure hits both conditions at once. Raised by
   * vericure-scichart-wasm from the retention constant; asserted in
   * max-accumulator-store-alignment.spec.ts rather than left as a prediction.
   *
   * Returns how many rows were folded and whether the accumulator is now level.
   * Idempotent: level in, zero folded, `closed: true` out.
   */
  catchUpTo(
    rows: {
      readonly firstIndex: number;
      readonly nextIndex: number;
      get(absIndex: number): TemperatureDataRow | undefined;
    },
    offset: number
  ): { folded: number; closed: boolean } {
    if (this.width === 0) {
      // Not configured: no fold can happen and none should be attempted. Not a
      // gap, so not an alarm.
      return { folded: 0, closed: this.folded >= rows.nextIndex };
    }
    if (rows.firstIndex > this.folded) {
      console.error(
        'CURING-ACC cannot close the gap: rows ' +
          this.folded +
          '..' +
          (rows.firstIndex - 1) +
          ' are needed but have been evicted (store ' +
          rows.firstIndex +
          '..' +
          (rows.nextIndex - 1) +
          '). The running max needs a fresh backfill; it is NOT level and any ' +
          'read at the newest index will miss.'
      );
      return { folded: 0, closed: false };
    }
    let folded = 0;
    for (let i = this.folded; i < rows.nextIndex; i++) {
      const row = rows.get(i);
      if (!row) {
        // A hole inside the retained range — not the eviction case above, and
        // not something the buffer should be able to produce. Stop rather than
        // fold out of sequence, and say so.
        console.error(
          'CURING-ACC hole in the retained range at ' +
            i +
            ' (store ' +
            rows.firstIndex +
            '..' +
            (rows.nextIndex - 1) +
            '); stopping short rather than folding out of sequence.'
        );
        return { folded, closed: false };
      }
      this.fold(row, offset);
      folded++;
    }
    return { folded, closed: this.folded >= rows.nextIndex };
  }

  /**
   * Fold a whole backfill response from tick 0. This is the ONLY correct way to
   * (re)establish the cumulative state, because the folds run from the start of
   * the cure. O(ticks x width), but once per backfill — not per poll.
   */
  rebuildFrom(
    rows: readonly TemperatureDataRow[],
    offset: number,
    width: number,
    thresholds: CuringThresholds
  ): void {
    this.configure(width, thresholds);
    for (const row of rows) {
      this.fold(row, offset);
    }
  }
}

/**
 * Retention for the Curing screen.
 *
 * Curing holds more per tick than any other screen: the raw rows plus up to six
 * derived snapshot series. At the 22,000-point worst case that is
 * 7 x N x 176 KB, so N is set lower than the other screens to keep the whole
 * screen inside a comparable budget:
 *   N = 200 -> ~246 MB with all four heat inputs enabled, ~140 MB with one.
 * 200 ticks is ~17 min at a 5 s cadence; older ticks refetch on scrub-back.
 */
export const CURING_TICK_RETENTION = 200;
